import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { FileText, ListChecks, MessageSquare, Network, Search } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useHostedConversations } from "@/lib/chat/product/hostedChat";
import { useHostedLoops } from "@/lib/loops/product/hostedLoops";

/**
 * Command/Ctrl + K palette across destinations, hosted conversations and
 * hosted Loops. WP-PT — both lists are authoritative hosted reads.
 */
export function SearchCommand() {
  const conversations = useHostedConversations();
  const hosted = useHostedLoops();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const go = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search conversations and Loops" />
      <CommandList>
        <CommandEmpty>Nothing matched.</CommandEmpty>
        <CommandGroup heading="Go to">
          <CommandItem onSelect={() => go(() => void navigate({ to: "/app/search" }))}>
            <Search className="size-4" aria-hidden="true" /> Full search
          </CommandItem>
          <CommandItem onSelect={() => go(() => void navigate({ to: "/app/queue" }))}>
            <ListChecks className="size-4" aria-hidden="true" /> Resolution Queue
          </CommandItem>
          <CommandItem onSelect={() => go(() => void navigate({ to: "/app/chaos-map" }))}>
            <Network className="size-4" aria-hidden="true" /> Chaos Map
          </CommandItem>
        </CommandGroup>
        <CommandGroup heading="Conversations">
          {conversations.conversations
            .filter((c) => !c.archivedAt)
            .slice(0, 6)
            .map((c) => (
              <CommandItem
                key={c.id}
                value={`conversation ${c.title}`}
                onSelect={() =>
                  go(
                    () =>
                      void navigate({
                        to: "/app/chat/$conversationId",
                        params: { conversationId: c.id },
                      }),
                  )
                }
              >
                <MessageSquare className="size-4" aria-hidden="true" /> {c.title}
              </CommandItem>
            ))}
        </CommandGroup>
        <CommandGroup heading="Loops">
          {hosted.loops.slice(0, 6).map((l) => (
            <CommandItem
              key={l.id}
              value={`loop ${l.title}`}
              onSelect={() =>
                go(() => void navigate({ to: "/app/loops/$loopId", params: { loopId: l.id } }))
              }
            >
              <FileText className="size-4" aria-hidden="true" /> {l.title}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
